import * as React from 'react';
import { useHistory, useLocation } from 'react-router';
import styled, { createGlobalStyle } from 'styled-components';
import { animated } from 'react-spring';

import { useFadeTransition } from '../../../utils/hooks/useFadeTransition';
import { Seo } from '../../../utils/seo/Index';

import { Container } from '../../layout/Container';
import { Heading } from '../../ui/text/Heading';
import { Paragraph } from '../../ui/text/Paragraph';
import { Hero } from '../../ui/hero/Index';
import { ButtonGroup } from '../../ui/button/ButtonGroup';
import { Button } from '../../ui/button/Button';
import { Logo } from '../../ui/logo/office/Index';
import { useQuery } from '@apollo/client';

import { STILLINGER_QUERY } from '../../../../client/__graphql__/queries';
import { Pagination } from '../../ui/pagination/Pagination';
import { SectionLink } from '../../ui/link/SectionLink';
import { ButtonLink } from '../../ui/link/ButtonLink';
import { FooterLogo } from '../../ui/logo/footer/Logo';

const Stillinger: React.FC = () => {
  const location = useLocation();

  const itemsPerPage: number = 3;
  const [page, setPage] = React.useState<number>(0);

  const { data, loading } = useQuery(STILLINGER_QUERY, {
    fetchPolicy: 'network-only',
    variables: {
      input: {
        pagination: {
          page: page,
          limit: itemsPerPage
        }
      }
    }
  });

  const { fadeIn: pageFadeIn } = useFadeTransition({
    loaded: !loading
  });

  return (
    <>
      <GlobalStyles />
      <Seo
        title="Er du en PrivatMegler?"
        description="Ledige stillinger i PrivatMegleren"
        image="https://cdn.reeltime.no/pm_assets/stillinger/PM_stillinger_bg_03.jpg"
        url={`https://privatmegleren.no/${location.pathname}`}
      />
      <Hero
        imageUrl="https://cdn.reeltime.no/pm_assets/stillinger/PM_stillinger_bg_01.jpg"
        opacity={0.6}
        height="500px"
        className="flex"
      >
        <HeroContent style={{ ...pageFadeIn }}>
          <Container>
            <Heading tag="h1" center={true}>
              Er du en PrivatMegler?
            </Heading>
            <Paragraph center={true}>
              Det finnes meglere – og det finnes PrivatMeglere. Alle våre
              meglere og medarbeidere blir håndplukket ut ifra sin faglige
              kompetanse og lidenskap. De har alle x-faktoren som kreves for å
              være blant de aller beste, og legger prestisje i å levere over
              forventning for våre kunder.
            </Paragraph>
          </Container>
        </HeroContent>
      </Hero>
            <Line style={{ ...pageFadeIn }} />
      <Container style={{ paddingTop: '50px', ...pageFadeIn }}>
      <GridContainer>
        <EmbedContainer>
          <iframe
            className="VimeoOuterContainer"
            src="https://player.vimeo.com/video/1114252852"
            frameBorder="0"
            width="100%"
            height="100%"
            allow="fullscreen; picture-in-picture"
          ></iframe>
        </EmbedContainer>
        <EmbedContainer>
          <iframe
            className="VimeoOuterContainer"
            src="https://player.vimeo.com/video/1114250760"
            frameBorder="0"
            width="100%"
            height="100%"
            allow="fullscreen; picture-in-picture"
          ></iframe>
        </EmbedContainer>
        <EmbedContainer>
          <iframe
            className="VimeoOuterContainer"
            src="https://player.vimeo.com/video/1114260817"
            frameBorder="0"
            width="100%"
            height="100%"
            allow="fullscreen; picture-in-picture"
          ></iframe>
        </EmbedContainer>
        <EmbedContainer>
          <iframe
            className="VimeoOuterContainer"
            src="https://player.vimeo.com/video/1114261404"
            frameBorder="0"
            width="100%"
            height="100%" 
            allow="fullscreen; picture-in-picture"
          ></iframe>
          </EmbedContainer>
          </GridContainer>
        <StillingSection>
          {data?.getPositions?.list?.map((item: any) => {
            return (
              <Stilling
                key={`stilling_${item.id}`}
                title={item.title}
                description={item.description}
                office={item.office.name}
                readMoreUrl={item.slug}
              />
            );
          })}
          {!loading && data?.getPositions?.total > itemsPerPage ? (
            <PaginationWrapper>
              <Pagination
                center={true}
                currentPage={page}
                maxPages={10}
                total={data?.getPositions?.total}
                itemsPerPage={itemsPerPage}
                pageOnChange={({ selected }) => {
                  let p = selected;
                  if (p !== page) {
                    setPage(p);
                  }
                }}
              />
            </PaginationWrapper>
          ) : null}
        </StillingSection>
      </Container>
      <Line style={{ ...pageFadeIn }} />
      <Container style={{ ...pageFadeIn, paddingTop: '180px' }}>
        <HartSection>
          <Hart />
        </HartSection>
        <HeadingSection>
          <Heading tag="h2" center={true}>
            Det handler om lidenskap
          </Heading>
        </HeadingSection>
        <TextSection>
          <TextColumn>
            For de fleste er kjøp og salg av eiendom en sjelden, omfattende og
            følelsesstyrt begivenhet. Derfor er det viktig å velge en megler som
            kan prestere på det høyeste nivået. For PrivatMegleren er faglig
            prestasjon en lidenskap. Integritet, fleksibilitet, vinnervilje,
            teft og humør er kvaliteter og egenskaper som kjennetegner alle våre
            medarbeidere.
          </TextColumn>
          <TextColumn>
            Et bevis på at dette gir resultater, er at syv av ti kunder kommer
            til oss på anbefaling fra tidligere kunder. Velger man oss som
            partner, skal man alltid kunne forvente det lille ekstra.
          </TextColumn>
        </TextSection>
        <ButtonGroup
          className="button-group center"
          style={{ marginBottom: '50px' }}
        >
          <Button as="link" type="tertiary" to="/om-oss/konsept">
            Les mer om hvem vi er
          </Button>
        </ButtonGroup>
      </Container>
      <CardSection>
        <Container>
          <CardsdWrapper>
            {/*             <Card>
              <Heading tag="h3" className="heading" center={true}>
                KLiKK X
              </Heading>
              <div className="card-content-box">
                <Paragraph className="paragraph" center={true}>
                  Skreddersydd annonsering basert på kunstig intelligens og
                  maskinlæring.
                </Paragraph>
                <ButtonGroup className="button-group center">
                  <Button
                    type="secondary"
                    onClick={() => {
                      window.location.href = 'https://privatmegleren.no/klikk';
                    }}
                  >
                    Les mer
                  </Button>
                </ButtonGroup>
              </div>
            </Card> */}
            <Card>
              <Heading tag="h3" className="heading" center={true}>
                Kjøpsmegling
              </Heading>
              <div className="card-content-box">
                <Paragraph className="paragraph" center={true}>
                  Vi kan nå også tilby våre kunder bistand ved kjøp av eiendom
                  og eiendom.
                </Paragraph>
                <ButtonGroup className="button-group center">
                  <Button as="link" type="secondary" to="/kjop">
                    Les mer
                  </Button>
                </ButtonGroup>
              </div>
            </Card>
            <Card>
              <Heading tag="h3" className="heading" center={true}>
                Boligbytte
              </Heading>
              <div className="card-content-box">
                <Paragraph className="paragraph" center={true}>
                  Vår unike og dynamiske prosess gjør det enda enklere for våre
                  kunder å flytte.
                </Paragraph>
                <ButtonGroup className="button-group center">
                  <Button as="link" type="secondary" to="/boligbytte">
                    Les mer
                  </Button>
                </ButtonGroup>
              </div>
            </Card>
          </CardsdWrapper>
        </Container>
        <CardSectionGradientTop />
        <CardSectionBackground />
      </CardSection>
    </>
  );
};

export default Stillinger;

const GlobalStyles = createGlobalStyle`
    .button-group {
      display: inline-grid;
    }
    .button-group-mobile {
        @media all and (max-width: 800px) {
            display: inline-grid !important;
            justify-content: center;
        }
    }
    .heading-opportunities {
      font-size: 35px;

      @media all and (max-width: 800px) {
        font-size: 25px;
      }
    }
    .footer {
        margin-top: 0;
    }
`;

const StillingerStyle = styled.div`
  margin-top: 5em;
`;

const HeadingSection = styled.div`
  margin-top: 20em;
`;

const IngressText = styled.div`
  font-size: 24px;
  color: white;
  text-align: center;
`;

const HeroContent = styled(animated.div)`
  display: flex;
  flex-flow: column;
  width: 100%;
  padding: 0 10px 0 10px;
`;

const StillingSection = styled.div`
  display: block;
  width: 100%;
  padding-bottom: 50px;
  @media all and (max-width: 450px) {
    margin-top: 0em;
  }
`;

const TextSection = styled.div`
  padding: 30px 0 80px 0;
  margin-bottom: 30px;
  display: grid;
  grid-template-columns: 1fr 1fr;
  grid-column-gap: 20px;

  color: rgb(255, 255, 255);
  border-bottom: solid 1px ${({ theme }) => theme.colors.primary};

  @media all and (max-width: 840px) {
    grid-template-columns: 1fr;
    grid-column-gap: 0;
    grid-row-gap: 20px;
  }
`;

const TextColumn = styled.div`
  @media all and (max-width: 840px) {
    text-align: center;
  }
`;

const CardSection = styled.div`
  display: flex;
  align-items: center;
  position: relative;
  padding: 50px 0;
  width: 100%;
  height: auto;
`;

const CardsdWrapper = styled.div`
  position: relative;
  width: 100%;
  padding: 100px 0;
  display: grid;
  grid-template-columns: 1fr 1fr;
  grid-column-gap: 40px;
  z-index: 1;

  @media all and (max-width: 960px) {
    grid-template-columns: 1fr;
    grid-column-gap: 0;
    grid-row-gap: 40px;
  }
`;

const CardSectionGradientTop = styled.div``;

const CardSectionBackground = styled.div`
  position: absolute;
  width: 100%;
  height: 100%;
  background: url('https://cdn.reeltime.no/pm_assets/stillinger/PM_stillinger_bg_02.jpg')
    no-repeat;
  background-size: cover;
  background-position: center;
  opacity: 0.8;
`;

const HartSection = styled.div`
  position: relative;
  width: 100%;
`;

const Hart = styled.div`
  position: absolute;
  top: -180px;
  left: 0;
  right: 0;
  width: 100%;
  height: 500px;
  background: url('https://cdn.reeltime.no/pm_assets/stillinger/PMhjerte.gif')
    no-repeat;
  background-size: contain;
  background-position: center;
  z-index: -1;
`;

const Card = styled.div`
  width: 100%;
  height: 200px;

  .heading {
    margin: 0 auto 10px auto;
  }

  .paragraph {
    margin-top: 0;
  }

  .card-content-box {
    padding: 20px;
    background: rgba(0, 0, 0, 0.3);
  }
`;

interface IStilling {
  title: string;
  office: string;
  description: string;
  readMoreUrl: string;
}

const Stilling: React.FC<IStilling> = ({
  title,
  description,
  office,
  readMoreUrl
}) => {
  return (
    <StillingWrapper>
      <StillingColumn className="logo">
        <Logo name={office} alt="bilde av logo" />
      </StillingColumn>
      <StillingColumn className="content">
        <h3>{title}</h3>
        {/*<h5>PrivatMegleren {office}</h5>*/}
        <p>{description}</p>
        <ButtonGroup className="button-group-mobile">
          <Button
            as="link"
            type="secondary"
            colorTheme="gold"
            to={`/stillinger/${readMoreUrl}`}
          >
            Les mer
          </Button>
        </ButtonGroup>
      </StillingColumn>
    </StillingWrapper>
  );
};

const StillingWrapper = styled.div`
  display: flex;
  width: 100%;
  padding: 10px 10px 30px 10px;
  border-top: solid 1px ${({ theme }) => theme.colors.primary};

  &:first-child {
    border-top: none;
  }

  @media all and (max-width: 800px) {
    flex-flow: column;
  }
`;

const StillingColumn = styled.div`
  padding: 20px;

  h3 {
    color: white;
    margin-bottom: 0;
  }
  h5 {
    margin-top: 5px;
    margin-bottom: 10px;
    font-size: 16px;
  }

  p {
    margin-bottom: 20px;
  }

  &.content {
    flex-flow: column;
  }

  @media all and (max-width: 800px) {
    display: flex;
    justify-content: center;
    align-items: center;
    padding: 0;

    &.content {
      text-align: center;
    }
  }

  &.logo {
    display: flex;
    align-items: center;
    flex: 0 0 350px;

    @media all and (max-width: 800px) {
      flex: 0 0 150px;
    }
  }
`;

const PaginationWrapper = styled.div`
  width: 100%;
  padding: 0;
`;

const LineAdjuster = styled.div`
  margin-top: -7em;
  @media all and (max-width: 450px) {
    margin-top: 33em;
  }
`;

const Line = styled(animated.div)`
  display: block;
  width: 100%;
  height: 1px;
  background: ${({ theme }) => theme.colors.primary};
`;

const EmbedContainer = styled.div`
  position: relative;
  padding: 58.07% 0 0 0;
  .VimeoOuterContainer {
    position: absolute;
    top: 0;
    left: 0;
    padding-bottom: 4em;
  }

  @media all and (max-width: 1200px) {
    padding: 73.07% 0 0 0;
    margin-top: 0px;
    .VimeoOuterContainer {
      padding-bottom: 4em;
    }
  }
`;

const GridContainer = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
  gap: 20px;
  margin-top: 20px;
`;
